import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { findAllOrphanedFiles, deleteOrphanedFiles, getOrphanedFilesStats } from "@/utils/improvedOrphanFileCleanup";
import { discoverStorageBuckets } from "@/utils/bucketDiscovery";
import { Trash, RefreshCw, FileX, HardDrive, Calendar, Loader, Database, CheckCircle } from "lucide-react";

interface OrphanedFile {
  bucket: string;
  path: string;
  size: number;
  created_at: string;
}

export const OrphanFileCleanup = () => {
  const [orphanedFiles, setOrphanedFiles] = useState<OrphanedFile[]>([]);
  const [isScanning, setIsScanning] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [bucketInfo, setBucketInfo] = useState<string>("");
  const { toast } = useToast();

  const scanForOrphanedFiles = async () => {
    setIsScanning(true);
    setBucketInfo("");
    
    try {
      // First discover accessible buckets
      const buckets = await discoverStorageBuckets();
      setBucketInfo(`Checked ${buckets.length} accessible storage buckets: ${buckets.map(b => b.name).join(', ')}`);
      
      // Then find orphaned files
      const files = await findAllOrphanedFiles();
      setOrphanedFiles(files);
      setSelectedFiles(new Set());
      
      toast({
        title: "Scan Complete",
        description: `Found ${files.length} orphaned files across ${buckets.length} accessible buckets`,
      });
    } catch (error) {
      console.error("Error scanning for orphaned files:", error);
      toast({
        title: "Scan Failed",
        description: "Failed to scan for orphaned files. Check console for details.",
        variant: "destructive",
      });
    } finally {
      setIsScanning(false);
    }
  };

  const deleteSelectedFiles = async () => {
    const filesToDelete = orphanedFiles.filter(file => 
      selectedFiles.has(`${file.bucket}/${file.path}`)
    );
    
    if (filesToDelete.length === 0) {
      toast({
        title: "No Files Selected",
        description: "Please select files to delete",
        variant: "destructive",
      });
      return;
    }

    setIsDeleting(true);
    setDeleteProgress(0);

    try {
      for (let i = 0; i < filesToDelete.length; i++) {
        await deleteOrphanedFiles([filesToDelete[i]]);
        setDeleteProgress(((i + 1) / filesToDelete.length) * 100);
      }

      // Remove deleted files from the list
      const remainingFiles = orphanedFiles.filter(file => 
        !selectedFiles.has(`${file.bucket}/${file.path}`)
      );
      setOrphanedFiles(remainingFiles);
      setSelectedFiles(new Set());

      toast({
        title: "Cleanup Complete",
        description: `Successfully deleted ${filesToDelete.length} orphaned files`,
      });
    } catch (error) {
      console.error("Error deleting orphaned files:", error);
      toast({
        title: "Cleanup Failed",
        description: "Some files may not have been deleted. Check console for details.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setDeleteProgress(0);
    }
  };

  const toggleFileSelection = (fileKey: string) => {
    const newSelection = new Set(selectedFiles);
    if (newSelection.has(fileKey)) {
      newSelection.delete(fileKey);
    } else {
      newSelection.add(fileKey);
    }
    setSelectedFiles(newSelection);
  };

  const selectAllFiles = () => {
    if (selectedFiles.size === orphanedFiles.length) {
      setSelectedFiles(new Set());
    } else {
      setSelectedFiles(new Set(orphanedFiles.map(file => `${file.bucket}/${file.path}`)));
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString();
  };

  const stats = getOrphanedFilesStats(orphanedFiles);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Orphan File Cleanup</h2>
          <p className="text-gray-600">Manage unused files in storage buckets</p>
        </div>
        <Button
          onClick={scanForOrphanedFiles}
          disabled={isScanning}
          className="flex items-center gap-2"
        >
          {isScanning ? (
            <Loader className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          {isScanning ? "Scanning..." : "Scan for Orphaned Files"}
        </Button>
      </div>

      {/* Bucket Discovery Info */}
      {bucketInfo && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Bucket Status</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600">{bucketInfo}</p>
            <p className="text-xs text-gray-500 mt-1">
              Using predefined bucket list for improved reliability
            </p>
          </CardContent>
        </Card>
      )}

      {/* Statistics Cards */}
      {orphanedFiles.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Files</CardTitle>
              <FileX className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalCount}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Size</CardTitle>
              <HardDrive className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalSizeMB} MB</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Selected</CardTitle>
              <Trash className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{selectedFiles.size}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Bucket Statistics */}
      {orphanedFiles.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Files by Bucket</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Object.entries(stats.byBucket).map(([bucket, bucketStats]) => (
                <div key={bucket} className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <Badge variant="outline">{bucket}</Badge>
                    <p className="text-sm text-gray-600 mt-1">
                      {bucketStats.count} files • {formatFileSize(bucketStats.size)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Delete Progress */}
      {isDeleting && (
        <Card>
          <CardHeader>
            <CardTitle>Cleanup Progress</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Progress value={deleteProgress} className="w-full" />
              <p className="text-sm text-gray-600">
                Deleting files... {Math.round(deleteProgress)}% complete
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Files List */}
      {orphanedFiles.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Orphaned Files</CardTitle>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={selectAllFiles}
                  size="sm"
                >
                  {selectedFiles.size === orphanedFiles.length ? "Deselect All" : "Select All"}
                </Button>
                
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="destructive"
                      disabled={selectedFiles.size === 0 || isDeleting}
                      size="sm"
                    >
                      <Trash className="h-4 w-4 mr-2" />
                      Delete Selected ({selectedFiles.size})
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete Orphaned Files</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to delete {selectedFiles.size} orphaned files? 
                        This action cannot be undone and will permanently remove these files from storage.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={deleteSelectedFiles}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Delete Files
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <input
                      type="checkbox"
                      checked={selectedFiles.size === orphanedFiles.length && orphanedFiles.length > 0}
                      onChange={selectAllFiles}
                      className="rounded"
                    />
                  </TableHead>
                  <TableHead>File Path</TableHead>
                  <TableHead>Bucket</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orphanedFiles.map((file) => {
                  const fileKey = `${file.bucket}/${file.path}`;
                  return (
                    <TableRow key={fileKey}>
                      <TableCell>
                        <input
                          type="checkbox"
                          checked={selectedFiles.has(fileKey)}
                          onChange={() => toggleFileSelection(fileKey)}
                          className="rounded"
                        />
                      </TableCell>
                      <TableCell className="font-mono text-sm max-w-xs truncate">
                        {file.path}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{file.bucket}</Badge>
                      </TableCell>
                      <TableCell>{formatFileSize(file.size)}</TableCell>
                      <TableCell className="flex items-center gap-1">
                        <Calendar className="h-3 w-3 text-gray-400" />
                        {formatDate(file.created_at)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {orphanedFiles.length === 0 && !isScanning && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileX className="h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Orphaned Files Found</h3>
            <p className="text-gray-600 text-center mb-4">
              Click "Scan for Orphaned Files" to check for unused files in your storage buckets.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
